import { ref, computed, getCurrentInstance, onBeforeUnmount } from "vue";
import type { ComputedRef } from "vue";
import store from "@/store";
import { DataModules } from "@/store/modules/data/modules";
import { ProductTypes } from "@/data/enums/products";
import type { FilterOption } from "./types";
import type {
  IProductCategory,
  IBrand,
  ICurrency,
  IAccount
} from "@upmind-automation/types";

export interface UseCategoriesOptions {
  productTypes?: ProductTypes[];
  isBasketContext?: boolean;
  accountId?: IAccount["id"] | null;
  currencyId?: ICurrency["id"] | null;
  /** Brand override. Defaults to admin brandId from auth store. */
  brandId?: IBrand["id"];
}

export interface CategoryFlatOption {
  value: string;
  label: string;
}

export interface UseCategoriesReturn {
  /** Raw root categories with nested subcategories. */
  categoryTree: ComputedRef<IProductCategory[]>;
  /** Pre-flattened "Parent → Child" options ready for select filters. */
  flatOptions: ComputedRef<CategoryFlatOption[]>;
  /**
   * Tree-shaped FilterOption[] (with nested `children`) for the
   * vue-treeselect input via `f.category()`. Mirrors the legacy
   * `multilevelCategoriesAsOptions` shape.
   */
  treeOptions: ComputedRef<FilterOption[]>;
  isLoading: ComputedRef<boolean>;
  fetch: (ignoreStored?: boolean, withStagedImports?: boolean) => Promise<void>;
}

const DEFAULT_TYPES: ProductTypes[] = [
  ProductTypes.SINGLE_PRODUCT,
  ProductTypes.PRODUCT_BUNDLE
];

/**
 * Listing-side counterpart to MultilevelCategoriesFetcher.
 *
 * Plain-data only: no `optionsFunc()` callback patterns (which previously
 * caused a fetch loop when stored references slipped between renders).
 * Reads from the same Vuex `data/catalogueCategories` scope as the mixin
 * so existing mixin consumers stay unaffected. Cleans up its scope on
 * unmount.
 */
export function useCategories(
  options: UseCategoriesOptions = {}
): UseCategoriesReturn {
  const productTypes = options.productTypes ?? DEFAULT_TYPES;
  const isBasketContext = options.isBasketContext ?? false;
  const accountId = options.accountId ?? null;
  const currencyId = options.currencyId ?? null;
  const brandId = options.brandId ?? store.state.auth.admin.brandId;
  const vm = getCurrentInstance()?.proxy;

  const isFetching = ref(false);

  const scope = computed<string>(() =>
    store.getters[
      `data/${DataModules.CATALOGUE_CATEGORIES}/rootCategoriesScope`
    ](brandId, productTypes)
  );

  const categoryTree = computed<IProductCategory[]>(
    () =>
      store.getters[`data/listArray`]({
        storeModule: DataModules.CATALOGUE_CATEGORIES,
        scope: scope.value
      }) || []
  );

  function flatten(
    nodes: IProductCategory[],
    prefix = ""
  ): CategoryFlatOption[] {
    const out: CategoryFlatOption[] = [];
    for (const node of nodes) {
      const label = prefix ? `${prefix} → ${node.name}` : node.name;
      out.push({ value: String(node.id), label });
      const children = (node as { subcategories?: IProductCategory[] })
        .subcategories;
      if (children?.length) {
        out.push(...flatten(children, label));
      }
    }
    return out;
  }

  const flatOptions = computed<CategoryFlatOption[]>(() =>
    flatten(categoryTree.value)
  );

  function toTreeOptions(nodes: IProductCategory[]): FilterOption[] {
    return nodes.map(node => {
      const children = (node as { subcategories?: IProductCategory[] })
        .subcategories;
      const opt: FilterOption = {
        value: String(node.id),
        label: node.name
      };
      if (children?.length) {
        opt.children = toTreeOptions(children);
      }
      return opt;
    });
  }

  const treeOptions = computed<FilterOption[]>(() =>
    toTreeOptions(categoryTree.value)
  );

  const isLoading = computed(() => isFetching.value);

  async function fetch(ignoreStored = false, withStagedImports = false) {
    isFetching.value = true;
    try {
      await store.dispatch(`data/${DataModules.CATALOGUE_CATEGORIES}/list`, {
        productTypes,
        scope: scope.value,
        isBasketContext,
        params: {
          order: "order",
          limit: 0,
          "filter[level]": 1,
          ...(withStagedImports ? { with_staged_imports: 1 } : {}),
          brandId,
          ...(currencyId ? { currency_id: currencyId } : {}),
          ...(accountId ? { account_id: accountId } : {})
        },
        vm,
        ignoreStored
      });
    } finally {
      isFetching.value = false;
    }
  }

  onBeforeUnmount(() => {
    store.dispatch("data/binList", {
      storeModule: DataModules.CATALOGUE_CATEGORIES,
      scope: scope.value,
      vm
    });
  });

  return { categoryTree, flatOptions, treeOptions, isLoading, fetch };
}
